import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

export function InterceptorsPage(): Node {
  return html`
    <div>
      <h1>HTTP Interceptors</h1>
      <p>Interceptors let you transform requests before they're sent, process responses before they reach your code, and handle errors globally.</p>

      <h2>Adding Interceptors</h2>
      ${CodeBlock(`import { createHttpClient } from 'onefold/http';

const http = createHttpClient({
  baseUrl: '/api',
  interceptors: {
    request: (config) => {
      // Add auth token to every request
      const token = localStorage.getItem('token');
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: \`Bearer \${token}\`,
        };
      }
      return config;
    },
    response: (response) => {
      // Log all responses
      console.log(\`[\${response.status}] \${response.url}\`);
      return response;
    },
    error: (error) => {
      // Global error handling
      if (error.status === 401) {
        navigate('/login');
      }
      throw error; // re-throw to propagate
    },
  },
});`)}

      <h2>Interceptor Pipeline</h2>
      <p>The execution order is:</p>
      <ol>
        <li><strong>Request interceptor</strong> — modify config (headers, body, URL) before fetch.</li>
        <li><strong>Network request</strong> — the actual HTTP call.</li>
        <li><strong>Response interceptor</strong> — process/transform successful responses.</li>
        <li><strong>Error interceptor</strong> — handle non-2xx responses or network failures.</li>
      </ol>

      ${Callout('Each interceptor must return the config/response (or a modified version). Forgetting to return will break the chain.')}

      <h2>Use Cases</h2>

      <h3>Token Refresh</h3>
      ${CodeBlock(`interceptors: {
  error: async (error) => {
    if (error.status === 401 && !error.config._retry) {
      error.config._retry = true;
      const newToken = await refreshToken();
      localStorage.setItem('token', newToken);
      return http.request(error.config); // retry
    }
    throw error;
  },
}`)}

      <h3>Request Timing</h3>
      ${CodeBlock(`interceptors: {
  request: (config) => {
    config._startTime = performance.now();
    return config;
  },
  response: (response) => {
    const duration = performance.now() - response.config._startTime;
    console.log(\`Request took \${duration.toFixed(0)}ms\`);
    return response;
  },
}`)}

      <h2>API</h2>
      <table>
        <tr><th>Interceptor</th><th>Signature</th><th>Description</th></tr>
        <tr><td><code>request</code></td><td>(config) => config</td><td>Modify request before sending.</td></tr>
        <tr><td><code>response</code></td><td>(response) => response</td><td>Process successful responses.</td></tr>
        <tr><td><code>error</code></td><td>(error) => throw | response</td><td>Handle errors globally.</td></tr>
      </table>

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/data/http-client">HTTP Client</a> — typed HTTP client setup and usage</li>
        <li><a href="/security/guards">RBAC Guards</a> — role-based access control for routes</li>
      </ul>
    </div>
  `;
}
